
import React, { useState, useEffect } from 'react';
import { Button, Space, Modal, Form, message, Input, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import "antd/dist/reset.css";
// 导入所有房源相关接口（新增批量删除）
import { getAllHouses, addHouse, updateHouse, deleteHouse, batchDeleteHouses, getUsers } from '../../api';
import ListToolbar from '../../components/ListToolbar';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';

const { Option } = Select;

const HouseList = () => {
  const [houses, setHouses] = useState([]);
  const [originHouses, setOriginHouses] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingHouse, setEditingHouse] = useState(null);
  const [isViewAdminModalVisible, setIsViewAdminModalVisible] = useState(false);
  const [currentHouse, setCurrentHouse] = useState(null); 
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [displayColumns, setDisplayColumns] = useState([]);
  const [userList, setUserList] = useState([]); // 用户列表，用于选择管理员

  // 加载房源数据（包含房东手机号）
  const fetchHouses = async () => {
    setLoading(true);
    try {
      const res = await getAllHouses();
      console.log('接口返回房源数据：', res);
      if (res?.success && Array.isArray(res.data)) { 
        setHouses(res.data);
        setOriginHouses(res.data);
        message.success(`加载成功 ${res.data.length} 个房源`);
      } else {
        message.error('加载房源失败：数据格式错误');
      }
    } catch (err) {
      const serverMsg = err.response?.data?.msg || err.response?.data?.error || err.response?.data?.message;
      console.error('接口请求失败：', err);
      console.error('服务器返回详情：', { status: err.response?.status, data: err.response?.data, serverMsg });
      message.error(serverMsg ? `加载房源失败：${serverMsg}` : '加载房源失败，请检查接口连接');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载数据
  useEffect(() => {
    fetchHouses();
  }, []);

  // 加载用户列表（用于选择管理员）
  const loadUserList = async () => {
    try {
      const res = await getUsers();
      const users = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setUserList(users);
    } catch (err) {
      console.error('加载用户列表失败：', err);
    }
  };

  // 搜索房源（含房东手机号、管理员手机号/昵称）
  const handleSearch = () => {
    if (!searchText.trim()) {
      setHouses(originHouses); 
      return;
    }
    const kw = searchText.trim();
    const filtered = originHouses.filter(house => {
      if (house.name?.includes(kw) || house.address?.includes(kw) ||
          house.landlordId?.toString().includes(kw) || house.landlordPhone?.includes(kw)) {
        return true;
      }
      const admins = house.admins || [];
      return admins.some(a => (a.phone && a.phone.includes(kw)) || (a.nickname && a.nickname.includes(kw)));
    });
    setHouses(filtered);
  };

  // 重置搜索
  const handleReset = () => {
    setSearchText('');
    setHouses(originHouses);
  };

  // 刷新数据
  const handleRefresh = () => {
    fetchHouses();
  };

  // 导出数据
  const handleExport = () => {
    const exportData = houses.map(house => {
      const adminStr = (house.admins || []).map(a => a.nickname || a.phone || `ID:${a.userId}`).join('、') || '-';
      return {
        '房源ID': house.id,
        '房源名称': house.name,
        '详细地址': house.address,
        '房东ID': house.landlordId,
        '房东手机号': house.landlordPhone || '-',
        '管理员': adminStr,
        '创建时间': house.createTime,
      };
    });

    if (exportToCSV(exportData, '房源列表')) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  // 打开新增/编辑弹窗（回显数据，含管理员）
  const showModal = (house = null) => {
    setEditingHouse(house);
    loadUserList(); // 加载用户列表供选择管理员
    if (house) {
      const adminIds = (house.admins || []).map(a => a.userId);
      form.setFieldsValue({
        name: house.name,
        address: house.address,
        landlordId: house.landlordId.toString(),
        adminUserIds: adminIds,
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  // 打开查看管理员弹窗
  const showViewAdminModal = (house) => {
    setCurrentHouse(house); 
    setIsViewAdminModalVisible(true);
  };

  // 提交表单（调用真实接口）
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      values.landlordId = parseInt(values.landlordId);
      setLoading(true);

      if (editingHouse) {
        // 编辑：传递管理员ID列表（adminUserIds 为数字数组）
        const adminUserIds = (values.adminUserIds || []).map(id => Number(id)).filter(id => id > 0);
        const payload = { name: values.name, address: values.address, landlordId: values.landlordId, adminUserIds };
        const res = await updateHouse(editingHouse.id, payload);
        if (res?.success) {
          message.success('编辑房源成功');
          fetchHouses();
        } else {
          message.error('编辑房源失败：' + (res?.msg || '后端返回异常'));
        }
      } else {
        // 新增：调用后端新增接口
        const res = await addHouse(values);
        if (res?.success) {
          message.success('新增房源成功');
          fetchHouses(); // 重新加载列表
        } else {
          message.error('新增房源失败：' + (res?.msg || '后端返回异常'));
        }
      }
      setIsModalVisible(false);
    } catch (err) {
      console.error('提交失败：', err);
      message.error('提交失败，请检查输入或接口连接');
    } finally {
      setLoading(false);
    }
  };

  // 单个删除房源（保留原有逻辑）
  const handleDelete = async (id) => {
    // 1. 打印日志，确认触发删除
    console.log('触发房源删除逻辑：ID=', id);
    
    // 2. 确认删除（用更稳定的提示，避免window.confirm被拦截）
    if (!window.confirm('确定要删除该房源吗？删除后不可恢复！')) {
      console.log('用户取消删除房源：ID=', id);
      return;
    }
    
    try {
      setLoading(true);
      console.log('开始调用房源删除接口：ID=', id);
      // 调用删除接口
      const res = await deleteHouse(id);
      console.log('房源删除接口返回：', res);
      
      // 3. 校验返回结果（和修改接口一致的判断逻辑）
      if (res?.success) {
        message.success('删除房源成功');
        fetchHouses(); // 重新加载列表
        setSelectedRowKeys([]); // 清空选中
      } else {
        const errMsg = res?.msg || '删除房源失败：后端返回异常';
        console.error('删除房源失败：', errMsg);
        message.error(errMsg);
      }
    } catch (err) {
      // 4. 详细的错误日志（关键！）
      console.error('房源删除接口调用失败详情：', err);
      // 解析axios错误（和用户删除一致）
      const errMsg = err.response?.data?.msg || err.message || '删除房源失败：网络/接口问题';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 新增：批量删除房源
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一个房源进行删除！');
      return;
    }
    
    // 确认批量删除
    if (!window.confirm(`确定要删除选中的${selectedRowKeys.length}个房源吗？删除后不可恢复，且会删除房源下所有房间、账单及账单生成规则！`)) {
      return;
    }
    
    try {
      setLoading(true);
      console.log('开始批量删除房源：ID列表=', selectedRowKeys);
      // 调用批量删除接口
      const res = await batchDeleteHouses({ houseIds: selectedRowKeys });
      console.log('批量删除房源接口返回：', res);
      
      if (res?.success) {
        message.success(`成功删除${selectedRowKeys.length}个房源`);
        fetchHouses(); // 重新加载列表
        setSelectedRowKeys([]); // 清空选中
      } else {
        const errMsg = res?.msg || '批量删除房源失败：后端返回异常';
        console.error('批量删除房源失败：', errMsg);
        message.error(errMsg);
      }
    } catch (err) {
      console.error('批量删除房源接口调用失败详情：', err);
      const errMsg = err.response?.data?.msg || err.message || '批量删除房源失败：网络/接口问题';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // 原始表格列配置
  const originalColumns = [
    { title: '房源ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: '房源名称', dataIndex: 'name', key: 'name', width: 150 },
    { title: '详细地址', dataIndex: 'address', key: 'address', width: 250 },
    { title: '房东ID', dataIndex: 'landlordId', key: 'landlordId', width: 80 },
    { title: '房东手机号', dataIndex: 'landlordPhone', key: 'landlordPhone', width: 120 },
    {
      title: '管理员',
      key: 'admins',
      width: 180,
      render: (_, record) => {
        const admins = record.admins || [];
        if (admins.length === 0) return <span style={{ color: '#999' }}>暂无</span>;
        return (
          <span>
            {admins.map((a, i) => (
              <Tag key={a.userId || i}>{a.nickname || a.phone || `ID:${a.userId}`}</Tag>
            ))}
          </span>
        );
      },
    },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 160 },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="middle">
          <Button 
            icon={<EyeOutlined />} 
            size="small" 
            type="default" 
            onClick={() => showViewAdminModal(record)}
          >
            查看管理员
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => showModal(record)}>编辑</Button>
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  // 从localStorage加载列设置并应用到表格（只处理隐藏）
  useEffect(() => {
    const storageKey = 'houseList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};
    
    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }
    
    // 根据列状态生成实际列配置（只处理隐藏）
    const processedColumns = originalColumns.map(col => {
      const state = columnStates[col.key];
      
      if (state === 'hidden') {
        return null;
      }
      
      return col;
    }).filter(col => col !== null);
    
    setDisplayColumns(processedColumns);
  }, []);

  // 表格多选配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
      console.log('选中的房源ID：', keys);
    },
    getCheckboxProps: (record) => ({
      disabled: false, // 所有行都可选中
      record,
    }),
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="房源管理" />
      
      {/* 统一的工具栏 */}
      <ListToolbar
        searchPlaceholder="搜索房源名称、地址、房东ID、房东手机号或管理员"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        showBatchDelete={true}
        selectedCount={selectedRowKeys.length}
        onBatchDelete={handleBatchDelete}
        showAdd={true}
        onAdd={() => showModal()}
      />

      {/* 房源表格（添加rowSelection开启多选） */}
      <ResizableTable
        columns={displayColumns.length > 0 ? displayColumns : originalColumns}
        dataSource={houses}
        rowKey="id"
        loading={loading}
        storageKey="houseList"
        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `共 ${total} 条数据` }}
        locale={{ emptyText: '暂无房源数据' }}
        bordered
        style={{ background: '#fff' }}
        scroll={{ x: 1500 }}
        rowSelection={rowSelection} // 开启多选
      />

      {/* 新增/编辑弹窗（修复antd v5警告） */}
      <Modal
        title={editingHouse ? '编辑房源' : '新增房源'}
        open={isModalVisible} // 替换visible→open
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={600}
        destroyOnHidden // 替换destroyOnClose→destroyOnHidden
        maskClosable={false} // 禁止点击遮罩关闭
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="房源名称"
            rules={[{ required: true, message: '请输入房源名称' }, { max: 100, message: '房源名称最多100个字' }]}
          >
            <Input placeholder="例如：阳光小区3号楼" maxLength={100} />
          </Form.Item>
          <Form.Item
            name="address"
            label="详细地址"
            rules={[{ required: true, message: '请输入详细地址' }, { max: 255, message: '详细地址最多255个字' }]}
          >
            <Input placeholder="例如：北京市朝阳区阳光小区3号楼" maxLength={255} />
          </Form.Item>
          <Form.Item
            name="landlordId"
            label="房东ID"
            rules={[
              { required: true, message: '请输入房东ID' },
              { pattern: /^\d+$/, message: '房东ID必须是数字' }
            ]}
          >
            <Input placeholder="例如：1001" maxLength={20} />
          </Form.Item>
          {/* 展示房东手机号（不可编辑） */}
          {editingHouse && (
            <Form.Item
              name="landlordPhone"
              label="房东手机号"
              initialValue={editingHouse.landlordPhone}
            >
              <Input disabled style={{ backgroundColor: '#f5f5f5' }} />
            </Form.Item>
          )}
          {/* 编辑时可选管理员（多选用户） */}
          {editingHouse && (
            <Form.Item
              name="adminUserIds"
              label="房源管理员"
              extra="选择可代管该房源的子管理员，房东本人无需选择"
            >
              <Select
                mode="multiple"
                placeholder="请选择管理员（可多选）"
                allowClear
                showSearch
                filterOption={(input, opt) =>
                  (opt?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
                }
                options={userList
                  .filter(u => u.id !== editingHouse.landlordId)
                  .map(u => ({
                    value: u.id,
                    label: `${u.nickname || u.phone || '-'}（ID:${u.id}）`,
                  }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 查看管理员弹窗（修复antd v5警告） */}
      <Modal
        title="查看管理员信息"
        open={isViewAdminModalVisible} // 替换visible→open
        onCancel={() => setIsViewAdminModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewAdminModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={500}
        destroyOnHidden // 替换destroyOnClose→destroyOnHidden
      >
        {currentHouse && (
          <div style={{ lineHeight: '2' }}>
            <p><strong>房源ID：</strong>{currentHouse.id}</p>
            <p><strong>房源名称：</strong>{currentHouse.name}</p>
            <p><strong>详细地址：</strong>{currentHouse.address}</p>
            <p><strong>房东ID：</strong>{currentHouse.landlordId}</p>
            <p><strong>房东手机号：</strong>{currentHouse.landlordPhone}</p>
            <p>
              <strong>管理员：</strong>
              {(currentHouse.admins || []).length === 0 ? (
                <span style={{ color: '#999' }}>暂无</span>
              ) : (
                <div style={{ marginTop: 4 }}>
                  {(currentHouse.admins || []).map((a, i) => (
                    <div key={a.userId || i} style={{ marginBottom: 4 }}>
                      用户ID: {a.userId}，昵称: {a.nickname || '-'}，手机号: {a.phone || '-'}
                    </div>
                  ))}
                </div>
              )}
            </p>
            <p><strong>创建时间：</strong>{currentHouse.createTime}</p>
            {currentHouse.updateTime && <p><strong>更新时间：</strong>{currentHouse.updateTime}</p>}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default HouseList;